/**
 * Normalize the backend `document_relations` expander into the version-family
 * groups the SEO head and the visible history list both need.
 *
 * The expander already computes the whole supersedes / superseded-by / related
 * graph (legacy backReferences port); it just never left the client-only
 * Semantic `Tab` in DocumentTabs, so a crawler saw none of it and the JSON-LD
 * carried no relations at all. This is the single place that graph is read, so
 * the structured data and the on-page links cannot describe different families.
 *
 * Shape consumed (content['@components'].document_relations.items):
 *   history: [ {title:'Supersedes',   value:[{docid,title,url,date,review_state}]},
 *              {title:'Superseded By', value:[...]} ]
 *   related: [ {title:'Related To',   value:[...]},
 *              {title:'Related From', value:[...]} ]
 *
 * Private / unresolved targets (no url, or review_state 'private') are dropped:
 * they are not public URLs, so they must not become crawlable links or
 * schema.org citations.
 */
import { flattenToAppURL } from '@plone/volto/helpers';

const clean = (list, origin) =>
  (list || [])
    .filter((it) => it && it.url && it.review_state !== 'private')
    .map((it) => {
      const href = flattenToAppURL(it.url);
      return {
        docid: it.docid || null,
        title: it.title || it.docid || href,
        date: it.date || null,
        href,
        abs: origin ? `${origin}${href}` : href,
      };
    });

/** Sub-entries of a relations group whose heading matches `re`, flattened. */
const pick = (group, re) =>
  (group || [])
    .filter((entry) => re.test(entry?.title || ''))
    .flatMap((entry) => entry.value || []);

/**
 * @returns {{supersedes, supersededBy, related}} each an array of
 *   {docid, title, date, href (app-relative), abs (absolute)}.
 */
export function extractRelations(content, site) {
  const rel = content?.['@components']?.document_relations;
  if (!rel?.items) {
    return { supersedes: [], supersededBy: [], related: [] };
  }
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  const history = rel.items.history || [];
  const related = rel.items.related || [];

  // The two history headings are "Supersedes" and "Superseded By"; anchoring
  // the forward match on the trailing "s" of "supersedes" keeps them distinct
  // (the reverse is "supersede*d* by").
  const supersededBy = clean(pick(history, /superseded\s*by/i), origin);
  const supersedes = clean(pick(history, /^supersedes\b/i), origin);
  const relatedAll = clean(
    [...pick(related, /related\s*to/i), ...pick(related, /related\s*from/i)],
    origin,
  );

  // De-dupe related against the version chain: a document that supersedes X
  // should not also list X under "related".
  const chain = new Set(
    [...supersedes, ...supersededBy].map((it) => it.href),
  );
  return {
    supersedes,
    supersededBy,
    related: relatedAll.filter((it) => !chain.has(it.href)),
  };
}

export default extractRelations;
